import * as React from "react";
import {
  ContentLayout,
  CustomTable,
  CustomDetailsList,
  Loader,
  HeaderBreadcrumbs,
  CustomeDialog,
} from "../../../shared";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { sortItems } from "../../../utils";
import { Button, Chip, Stack, Typography } from "@mui/material";
import moment from "moment";
import { useClientAccountTransactions } from "../hooks/useClientAccountTransactions";
import { MorePopOver } from "../../Orders";
import { RevertPaymentDialog, SendEmailsDialog } from "../components";
import { FilePreviewComponent } from "../components/FilePreviewComponent";

export const ClientAccountTransactionsDetails = () => {
  const location = useLocation();
  let url = location?.state?.from;
  const { id, t_id } = useParams();
  const navigate = useNavigate();
  const { isStoreAdmin,
    state,
    handleDownloadPDF,
    handleModal,
    handleInputChange,
    handleFileChange,
    handleSubmitRevertPayment,
    handleSendInvoiceByID,
    handleFileDelete,
  } = useClientAccountTransactions({ id: id, isList: false, tId: t_id, });

  const { isLoading, details, openSendEmaileDialog, isOpenRevertPayment } = state

  const columns = React.useMemo(
    () => [
      {
        Header: "order number",
        accessor: "transaction_id",
        sortType: (prev, curr, columnId) => {
          return sortItems(prev, curr, columnId);
        },
      },
      {
        Header: "TXN Date",
        disableSortBy: true,
        accessor: "transaction_date",
      },
      {
        Header: "Order Amount",
        disableSortBy: true,
        accessor: (value) => {
          return value?.order_amount ? `$${value.order_amount.toFixed(2)}` : '--';
        },
      },
      {
        Header: "Settled Amount",
        disableSortBy: true,
        accessor: (value) => {
          return value?.settled_amount ? `$${value.settled_amount.toFixed(2)}` : '--';
        },
      },
      {
        Header: "Balance",
        align: "right",
        accessor: (value) => {
          return value?.balance > 0 ? (
            <Typography color="#C42B1C" fontSize="14px">
              ${value?.balance?.toFixed(2)}
            </Typography>
          ) : (
            <Typography fontSize="14px">
              ${value?.balance?.toFixed(2)}
            </Typography>
          );
        },
        disableSortBy: true,
      },
    ],
    []
  );


  const columns2 = React.useMemo(
    () => [

      {
        Header: "TXN ID",
        disableSortBy: true,
        accessor: "transaction_id",
        Cell: ({ value, row }) => {
          return (
            <Typography 
              sx={{
                textDecoration: "underline",
                cursor: "pointer",
              }}
              onClick={() => 
                navigate(
                  `${isStoreAdmin ? '/store-admin' : ''}/client-account-v2/details/${id}/transactions/${value}`
                )
              }
            >
              {value}
            </Typography>
          );
        }
      },
      {
        Header: "Date",
        disableSortBy: true,
        accessor: "transaction_date",


      },
      {
        Header: "Amount",
        align: "right",
        disableSortBy: true,
        accessor: (value) => {
          return value?.amount ? `$${value.amount.toFixed(2)}` : '--';
        },
      },
    ],
    []
  );

  const detailsColumns = [
    {
      title: "Client Account Name",
      accessor: ({ client_account }) => {
        return <span>{client_account?.account_name}</span>
      },
    },
    {
      title: "Account No",
      accessor: ({ client_account }) => {
        return <span>{client_account?.number}</span>
      },
    },
    {
      title: "TXN Id",
      accessor: "transaction_id",
    },
    {
      title: "Reference Id",
      accessor: "reference_id",
    },
    {
      title: "Payment Media",
      accessor: "payment_method",
    },
    {
      title: ({ transaction_type }) => {
        return <span >{transaction_type === "Payment Reverted" ? "Reverted Amount" : "Amount"}</span>
      },
      accessor: ({ amount }) => {
        return <span style={{ fontWeight: 500 }}>${amount?.toFixed(2)}</span>
      },
    },
    {
      title: "TXN Type",
      accessor: (value) => {
        return (
          <Stack sx={{
            flexDirection: "row",
            gap: "8px",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
          }}
          >
            <Chip
              label={value?.transaction_type}
              color={
                value?.transaction_type === "Payment"
                  ? "success"
                  : value?.transaction_type === "Purchase"
                    ? "primary"
                    : value?.transaction_type === "Payment Reverted"
                      ? "warning"
                      : "error"
              }
              variant="outlined"
              size="small"
              sx={{
                border: "none !important",
                color: value?.transaction_type === "Payment Reverted" ? "#C07302" : "inherit",
                backgroundColor:
                  value?.transaction_type === "Purchase"
                    ? "rgba(21, 96, 212, 0.08)"
                    : value?.transaction_type === "Payment Reverted"
                      ? "#FFF4CE"
                      : "inherit",
              }}
            />
            {value?.is_reverted ? (
              <Stack
                onClick={() => window.open(`${isStoreAdmin ? '/store-admin' : ''}/client-account-v2/details/${id}/transactions/${value.reverted_details[0]?.transaction_id}`, '_blank')}
                sx={{
                  flexDirection: "row",
                  gap: "4px",
                  alignItems: "center",
                  height: "26px",
                  fontSize: "12px",
                  color: "#1560D4",
                  backgroundColor: "#1560D414",
                  padding: "4px 8px",
                  borderRadius: "10px",
                  cursor: "pointer",
                }}
              >
                <span>
                  Reverted
                </span>
                <img style={{
                  width: '13px',
                  filter: "brightness(0) saturate(100%) invert(24%) sepia(93%) saturate(2244%) hue-rotate(209deg) brightness(90%) contrast(86%)"
                }} alt="link" src="/icons/ic_external_link.svg" />
              </Stack>
            ) : ""}
          </Stack>
        );
      },
    },
    {
      title: "TXN Date",
      accessor: "transaction_date",
    },
    {
      title: "Settlement Date",
      accessor: ({ settlement_date }) =>
        settlement_date ? moment(settlement_date).format("DD/MM/yy") : "",
    },
    {
      title: "Note",
      accessor: (row) => {
        if (row.transaction_type === "Payment Reverted") {
          return row.reverted_reason || "-";
        } else {
          return row.notes || "-";
        }
      }
    },
    {
      title: "Files",
      hidden: (row) => {
        return row.transaction_type !== "Payment Reverted";
      },
      accessor: ({ reverted_files }) => {
        return <FilePreviewComponent files={reverted_files} />;
      }
    },

  ];
  return (
    <>
      <ContentLayout
        title={details?.transaction_id}
        breadcrumbs={

          <HeaderBreadcrumbs
            links={[
              { name: "Dashboard", href: "/" },
              {
                name: "Client Account",
                href: isStoreAdmin
                  ? `/store-admin/client-account-v2`
                  : "/client-account-v2",
              },
              {
                name: details?.client_account?.account_name,
                href: isStoreAdmin
                  ? `/store-admin/client-account-v2/details/${id}`
                  : `/client-account-v2/details/${id}`,
              },
              {
                name: details?.transaction_id,
              },
            ]}
          />
        }
        rightContent={
          <Stack sx={{
            flexDirection: "row",
            gap: "6px"
          }}>
            {!details.is_reverted && details.transaction_type === "Payment" ? (
              <Button
                size="small"
                variant="text"
                color="error"
                onClick={() => handleModal('revert', true)}
              >
                Revert Payment
              </Button>
            ) : ""}
            <Button
              sx={{ width: "40px" }}
              type="button"
              size="small"
              variant="text"
              onClick={() => handleDownloadPDF("print")}
              disabled={state.invoiceLoading}
            >
              <img alt="tools" src="/icons/ic_print.svg" />
            </Button>
            <MorePopOver
              sendInvoiceLabel="Send Email"
              SendInvoice={() => handleModal('email', true)}
              DownloadInvoice={handleDownloadPDF}
            />
          </Stack>
        }

      >
        {isLoading ? (
          <Loader />
        ) : (
          <>
            <CustomDetailsList
              columns={detailsColumns}
              data={details}
              title="Transaction Details"
            />
            {!details?.refund_id && (
              <>
                {details?.transaction_type === "Payment Reverted" ? (
                  <CustomTable
                    columns={columns2}
                    data={details?.reverted_details ?? []}
                    searchBar={false}
                    tableTitle="Reverted Payment"
                    pagination={false}
                  />
                ) : (
                  <CustomTable
                    columns={columns}
                    data={details?.settlement_details ?? []}
                    searchBar={false}
                    tableTitle="Settled Transactions"
                    pagination={false}
                  />
                )}
              </>
            )}
          </>
        )}
      </ContentLayout>
      <SendEmailsDialog
        open={openSendEmaileDialog}
        key={openSendEmaileDialog}
        disableCloseOnBackgroundClick
        handleClose={() => handleModal("email", false)}
        helperText="The account statement will be sent to"
        submitting={false} //send button loading
        defaultEmails={[details?.client_account?.default_email ?? ""]} //default emails
        handleFormSubmit={handleSendInvoiceByID}
      />
      <CustomeDialog
        open={isOpenRevertPayment}
        handleClose={() => handleModal("revert", false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{
          sx: { width: "423px", minHeight: "400px", p: "16px" },
        }}
        title="Revert Payment"
        content={
          <RevertPaymentDialog
            state={state}
            handleInputChange={handleInputChange}
            handleFileChange={handleFileChange}
            handleClose={() => handleModal("revert", false)}
            onSubmit={handleSubmitRevertPayment}
            handleFileDelete={handleFileDelete}
          />
        }
      />
    </>
  );
};
